import { Suspense, lazy, useEffect } from "react"
import StickyHeader from "../../components/StickyHeader"
import LandingPage from "../../components/LandingPage"
import LazySection from "../../components/LazySection"
import { useProjects } from "../../hooks/useProjects"

const loadExperienceTimeline = () => import("../../components/ExperienceTimeline")
const loadProjectsSection = () => import("../../components/ProjectsSection")
const loadSkillsSection = () => import("../../components/SkillsSection")
const loadFooter = () => import("../../components/Footer")

const ExperienceTimeline = lazy(loadExperienceTimeline)
const ProjectsSection = lazy(loadProjectsSection)
const SkillsSection = lazy(loadSkillsSection)
const Footer = lazy(loadFooter)

const sectionLoaders = [
  loadExperienceTimeline,
  loadProjectsSection,
  loadSkillsSection,
  loadFooter,
]

export default function HomePage() {
  const { projects, isLoading, error } = useProjects()

  useEffect(() => {
    const preloadSections = () => {
      void Promise.allSettled(
        sectionLoaders.map((loadSection) => loadSection())
      )
    }

    if (typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(preloadSections, { timeout: 1500 })
      return () => window.cancelIdleCallback(idleId)
    }

    const timeoutId = window.setTimeout(preloadSections, 300)
    return () => window.clearTimeout(timeoutId)
  }, [])

  return (
    <div className="page-root">
      <StickyHeader />

      <main className="site-main">
        <LandingPage />

        <section id="experience" className="portfolio-content-section">
          <Suspense
            fallback={
              <div className="mx-auto my-12 w-full max-w-[var(--max-width)] px-4 py-6 text-muted">
                Loading experience...
              </div>
            }
          >
            <LazySection minHeight={320}>
              <ExperienceTimeline />
            </LazySection>
          </Suspense>
        </section>

        <section id="projects" className="portfolio-content-section">
          <Suspense
            fallback={
              <div className="mx-auto my-12 w-full max-w-[var(--max-width)] px-4 py-6 text-muted">
                Loading projects...
              </div>
            }
          >
            <LazySection minHeight={560}>
              <ProjectsSection
                projects={projects}
                isLoading={isLoading}
                error={error}
              />
            </LazySection>
          </Suspense>
        </section>

        <section id="skills" className="portfolio-content-section">
          <Suspense
            fallback={
              <div className="mx-auto my-12 w-full max-w-[var(--max-width)] px-4 py-6 text-muted">
                Loading skills...
              </div>
            }
          >
            <LazySection minHeight={420}>
              <SkillsSection projects={projects} />
            </LazySection>
          </Suspense>
        </section>
      </main>

      <Suspense
        fallback={
          <div className="mx-auto w-full max-w-[var(--max-width)] px-4 py-6 text-muted">
            Loading footer...
          </div>
        }
      >
        <LazySection minHeight={160}>
          <Footer />
        </LazySection>
      </Suspense>
    </div>
  )
}
